// Cron parsing + human summaries (T7.11). Pure functions, no dependencies —
// unit-test candidates (docs/frontend-tasks.md T22.1).

const FIELD_BOUNDS = {
  minute: { min: 0, max: 59 },
  hour: { min: 0, max: 23 },
  dom: { min: 1, max: 31 },
  month: { min: 1, max: 12 },
  dow: { min: 0, max: 6 }
};

const FIELD_ORDER = ['minute', 'hour', 'dom', 'month', 'dow'];

const PART_PATTERN = /^(\*|\*\/(\d+)|(\d{1,2})(-(\d{1,2}))?(\/(\d+))?)$/;

function expandField(raw, field) {
  const { min, max } = FIELD_BOUNDS[field];
  const allowed = new Set();

  for (const part of raw.split(',')) {
    const match = PART_PATTERN.exec(part);

    if (!match) {
      return null;
    }

    if (match[1] === '*') {
      for (let value = min; value <= max; value += 1) allowed.add(value);
      continue;
    }

    if (match[1].startsWith('*/')) {
      const step = Number(match[2]);

      if (!step) {
        return null;
      }

      for (let value = min; value <= max; value += step) allowed.add(value);
      continue;
    }

    const start = Number(match[3]);
    const end = match[5] !== undefined ? Number(match[5]) : start;
    const step = match[7] !== undefined ? Number(match[7]) : 1;

    if (start < min || end > max || start > end || step < 1) {
      return null;
    }

    for (let value = start; value <= end; value += step) allowed.add(value);
  }

  return allowed;
}

// Returns { minute, hour, dom, month, dow } as Sets of allowed values,
// or null when the expression is invalid.
export function parseCron(expression) {
  if (typeof expression !== 'string') {
    return null;
  }

  const fields = expression.trim().split(/\s+/);

  if (fields.length !== 5) {
    return null;
  }

  const parsed = {};

  for (let index = 0; index < 5; index += 1) {
    const field = FIELD_ORDER[index];
    const allowed = expandField(fields[index], field);

    if (!allowed || allowed.size === 0) {
      return null;
    }

    parsed[field] = allowed;
  }

  return parsed;
}

export function isValidCron(expression) {
  return parseCron(expression) !== null;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function formatTime(hourSet, minuteSet) {
  if (hourSet.size !== 1 || minuteSet.size !== 1) {
    return null;
  }

  const hour = hourSet.values().next().value;
  const minute = minuteSet.values().next().value;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function describeDayFilter(domSet, dowSet) {
  const domRestricted = domSet.size !== Object.keys(FIELD_BOUNDS.dom).length
    && domSet.size !== 31;
  const dowRestricted = dowSet.size !== 7;

  if (dowRestricted) {
    const days = [...dowSet].sort((a, b) => a - b).map((day) => DAY_NAMES[day]);

    if (days.length === 5 && [1, 2, 3, 4, 5].every((day) => dowSet.has(day))) {
      return 'weekdays';
    }

    return days.length === 1 ? `${days[0]}s` : days.join(', ');
  }

  if (domRestricted) {
    const days = [...domSet].sort((a, b) => a - b);
    return days.length === 1 ? `on day ${days[0]} of the month` : `on days ${days.join(', ')}`;
  }

  return null;
}

// "every weekday at 09:00" style summary. Falls back to the raw expression
// for shapes the heuristics do not cover.
export function describeCron(expression) {
  const parsed = parseCron(expression);

  if (!parsed) {
    return 'Invalid schedule';
  }

  const { minute, hour, dom, month, dow } = parsed;
  const time = formatTime(hour, minute);

  if (minute.size === 60 && hour.size === 24 && !describeDayFilter(dom, dow)) {
    return 'every minute';
  }

  if (hour.size === 24) {
    const minutes = [...minute].sort((a, b) => a - b);

    if (minutes.length > 1 && minutes[1] - minutes[0] === minutes[0]) {
      return `every ${minutes[0]} minutes`;
    }

    return 'hourly';
  }

  if (minute.size === 60 && hour.size === 1) {
    const hourValue = hour.values().next().value;
    return `every hour at :${String(hourValue).padStart(2, '0')}`;
  }

  const dayPart = describeDayFilter(dom, dow);

  if (time && dayPart) {
    return `${dayPart} at ${time}`;
  }

  if (time) {
    return `daily at ${time}`;
  }

  return `runs on schedule ${expression}`;
}

function matchesField(set, value) {
  return set.has(value);
}

// Next execution after `from` (exclusive). Brute-force minute scan capped at
// 14 days — fine for UI summaries, cheap relative to scheduling itself.
export function nextRunFromCron(expression, from = new Date()) {
  const parsed = parseCron(expression);

  if (!parsed) {
    return null;
  }

  const { minute, hour, dom, month, dow } = parsed;
  const cursor = new Date(from.getTime());
  cursor.setSeconds(0, 0);
  cursor.setMinutes(cursor.getMinutes() + 1);

  const MAX_MINUTES = 14 * 24 * 60;

  for (let step = 0; step < MAX_MINUTES; step += 1) {
    const domMatches = matchesField(dom, cursor.getDate());
    const dowMatches = matchesField(dow, cursor.getDay());

    // Standard cron dom/dow semantics: if BOTH are restricted, either may
    // match; if only one is restricted, it alone decides (an unrestricted
    // field must not force a match every day).
    const domRestricted = dom.size !== 31;
    const dowRestricted = dow.size !== 7;
    const dayMatches = domRestricted && dowRestricted
      ? domMatches || dowMatches
      : domRestricted
        ? domMatches
        : dowMatches;

    if (
      matchesField(month, cursor.getMonth() + 1)
      && dayMatches
      && matchesField(hour, cursor.getHours())
      && matchesField(minute, cursor.getMinutes())
    ) {
      return new Date(cursor.getTime());
    }

    cursor.setMinutes(cursor.getMinutes() + 1);
  }

  return null;
}
